
import { defineComponent, reactive, ref, watch, computed } from 'vue'
import { ElForm } from 'element-plus'
import { AxiosResponse } from 'axios'
import { useRouter } from 'vue-router'

import InputGroup from '@/components/layout/InputGroup.vue'
import JrText from '@/components/jr-parts/JrText.vue'
import JrDateTimePicker from '@/components/jr-parts/JrDateTimePicker.vue'
import JrDateRangePicker from '@/components/jr-parts/JrDateRangePicker.vue'
import InputKilo from '@/components/inputs/InputKilo.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import DepartmentSelect from '@/components/inputs/DepartmentSelect.vue'
import CollapseGroup from '@/components/layout/CollapseGroup.vue'
import JrMap from '@/components/jr-parts/JrMap.vue'
import LineSelect from '@/components/inputs/LineSelect.vue'
import StationSelect from '@/components/inputs/StationSelect.vue'
import ConstructionTypeSelect from '@/components/inputs/ConstructionTypeSelect.vue'
import JrRadioGroup, { availableLabels } from '@/components/jr-parts/JrRadioGroup.vue'
import FormItemDisabled from '@/components/layout/FormItemDisabled.vue'
import JrFileInput from '@/components/jr-parts/JrFileInput.vue'
import JrFileView from '@/components/jr-parts/JrFileView.vue'
import JrButtonForJr from '@/components/jr-parts/JrButtonForJr.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import MessageHistoryToMessageType from '@/components/others/MessageHistoryToMessageType.vue'
import JrGeocoder from '@/components/jr-parts/JrGeocoder.vue'
import ProximityDetailRegist from '@/components/actions/ProximityDetailRegist.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

import { isAfterToday } from '@/services/date'
import { FileTypes, RequestTypes, ConstructionTypes, APPLICANT_UPLOAD_FILE_EXTS } from '@/constants/enums'
import { RequestHeaderProximityDetailEditForm } from '@/types/forms'
import { ReadRequest } from '@/requests/requestHeader'
import { RequestHeaderDetail } from '@/api'
import { MessageTypes } from '@/constants/messageTypes'
import { proximityEditRule } from '@/constants/rules'
import { DEFAULT_CENTER_LAT, DEFAULT_CENTER_LNG, DEFAULT_ZOOM, PIN_ZOOM } from '@/constants/map'

export default defineComponent({
  emits: ['update:form'],
  name: 'ProximityInfo',
  components: {
    ConfirmDialog,
    InputGroup,
    JrText,
    JrDateTimePicker,
    InputKilo,
    InputLabel,
    DepartmentSelect,
    CollapseGroup,
    JrMap,
    LineSelect,
    StationSelect,
    JrDateRangePicker,
    ConstructionTypeSelect,
    JrRadioGroup,
    FormItemDisabled,
    JrFileInput,
    JrFileView,
    JrButtonForJr,
    JrButton,
    MessageHistoryToMessageType,
    JrGeocoder,
    ProximityDetailRegist,
  },
  props: {
    // 申請基本ID
    requestHeaderId: {
      type: Number,
      required: true,
    },
    // 編集可能か
    isEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
    // 申請番号・日時表示可否
    isShow: {
      type: Boolean,
      required: false,
      default: true,
    },
    // 一覧画面に戻るか
    returnToList: {
      type: Boolean,
      required: false,
      default: false,
    },
    // 変更・協議先連絡等のボタン表示
    isActionBtnVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
    isHistoryTableVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
    // 保存ボタン表示
    isEditBtnVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
    isShowConfirmDialog: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup(props, context) {
    const router = useRouter()
    const isShowDialog = ref<boolean>(false)
    const isValid = ref<boolean>(false)
    const isSave = ref<boolean>(false)
    const proximityId = ref<number | null>(null)
    const errorFoundationDrawingFile = ref<string>('')
    const errorWorkingRangeFile = ref<string>('')
    const formRef = ref<InstanceType<typeof ElForm>>()
    const mapCenterLat = ref<string>(DEFAULT_CENTER_LAT)
    const mapCenterLng = ref<string>(DEFAULT_CENTER_LNG)
    const mapZoom = ref<number>(DEFAULT_ZOOM)
    const changeMapCenter = () => {
      mapZoom.value = PIN_ZOOM
      // 中心座標変更時は工事場所住所をリセットする
      form.proximity.construction_latitude = ''
      form.proximity.construction_longitude = ''
    }
    /**
     * バックバリデーション
     **/
    // バックバリデーション型定義
    type backValidationType = {
      [key: string]: string
    }
    // 初期化したバックバリデーションメッセージ
    const initializedBackValid: backValidationType = {
      start_station: '',
      end_station: '',
      construction_type: '',
      use_heavy_remark: '',
      foundation_work_remark: '',
      without_protective_remark: '',
      end_plan_date: '',
      start_plan_date: '',
    }
    // バックバリデーション同期オブジェクト
    const validationError = ref<backValidationType>({ ...initializedBackValid })

    // GoogleMapを読み込むかどうか
    const mapLoad = ref<boolean>(false)
    const form = reactive<RequestHeaderProximityDetailEditForm>({
      request_header_id: undefined,
      history_comment: '新規登録',
      request_type: RequestTypes.PROXIMITY,
      applicant_number: '',
      applicant_time: '',
      department: -1,
      applicant_title: '',
      meter: undefined,
      applicant_email: null,
      applicant_business_name: '',
      applicant_manager_name: '',
      applicant_address: '',
      applicant_tel: '',
      is_tanto_department_login_user: false,
      proximity: {
        request_header: -1,
        construction_address: '',
        construction_building_name: '',
        line: '',
        line_name: '',
        start_station: -1,
        start_station_name: '',
        end_station: -1,
        end_station_name: '',
        degree: '',
        construction_latitude: '',
        construction_longitude: '',
        construction_detail: '',
        construction_type: -1,
        construction_type_other: null,
        construction_purpose: '',
        is_use_heavy: false,
        use_heavy_remark: null,
        is_foundation_work: false,
        foundation_work_remark: null,
        is_ground_completion: false,
        is_use_out_land: false,
        is_without_protective: false,
        without_protective_remark: null,
        start_plan_date: '',
        end_plan_date: '',
        location_map_file: -1,
        location_map_file_selected: false,
        ground_plan_file: -1,
        ground_plan_file_selected: false,
        cross_section_file: -1,
        cross_section_file_selected: false,
        actual_place_photo_file: -1,
        actual_place_photo_file_selected: false,
        foundation_drawing_file: null,
        foundation_drawing_file_selected: false,
        plan_temporary_work_file: null,
        plan_temporary_work_file_selected: false,
        working_range_file: null,
        working_range_file_selected: false,
        cadastral_map_file: null,
        cadastral_map_file_selected: false,
        survey_drawing_of_acreage_file: null,
        survey_drawing_of_acreage_file_selected: false,
      },
    })

    // ファイル関連
    const location_map_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const ground_plan_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const cross_section_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const actual_place_photo_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const foundation_drawing_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const plan_temporary_work_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const working_range_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const cadastral_map_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const survey_drawing_of_acreage_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const fileRefs = [
      location_map_file_ref,
      ground_plan_file_ref,
      cross_section_file_ref,
      actual_place_photo_file_ref,
      foundation_drawing_file_ref,
      plan_temporary_work_file_ref,
      working_range_file_ref,
      cadastral_map_file_ref,
      survey_drawing_of_acreage_file_ref,
    ]

    //履歴表示対象のメッセージ種別
    const targetMessageTypes: number[] = [
      MessageTypes.P_APPLICANT_CALL,
      MessageTypes.P_APPLICANT_CALL_RECV,
      MessageTypes.P_APPLICANT_CALL_SENDER_CALL,
      MessageTypes.APPLICANT_REGIST_PROXIMITY,
      MessageTypes.CHANGE_PROXIMITY_NOTICE,
      MessageTypes.PROXY_REGIST_PROXIMITY_NOTICE,
    ]

    // 初回とID変更時に情報を取得する
    watch(
      () => props.requestHeaderId,
      async (id: number) => {
        proximityId.value = null
        if (id) {
          const request = new ReadRequest(id)
          await request.exec()
          if (request.success) {
            const { data } = request.getResponse() as AxiosResponse<RequestHeaderDetail>
            if (data.request_type !== RequestTypes.PROXIMITY) {
              // 近接工事では無ければ404へ飛ばす
              router.push({ name: 'NotFound' })
              return
            }
            const proximity = request.getProximity()
            proximityId.value = proximity.proximity.proximity_id as number
            // 取得した情報をformに入れる
            Object.assign(form, proximity)
            mapZoom.value = PIN_ZOOM
            mapCenterLat.value = proximity.proximity.construction_latitude
            mapCenterLng.value = proximity.proximity.construction_longitude
          }
        }
      },
      { immediate: true }
    )

    // form変更時はemitする
    watch(
      () => form,
      () => {
        context.emit('update:form', form)
      },
      { deep: true }
    )

    watch(
      () => isValid.value,
      () => {
        if (isValid.value) {
          isSave.value = false
          if (!validate()) {
            // フロントバリデーション失敗時
            isValid.value = false
            return false
          }

          if (props.isShowConfirmDialog) {
            // 申請変更時
            isShowDialog.value = true
          } else {
            // 申請登録時
            isSave.value = true
          }
        }
      }
    )
    watch(
      () => [form.proximity.working_range_file_selected, form.proximity.is_use_heavy],
      () => {
        errorWorkingRangeFile.value = ''
      }
    )
    watch(
      () => [form.proximity.foundation_drawing_file_selected, form.proximity.is_foundation_work],
      () => {
        errorFoundationDrawingFile.value = ''
      }
    )

    // 戻り先
    const returnTo = computed(() => {
      if (props.returnToList) {
        let screen = ''
        if (props.isEdit) {
          screen = 'add'
        } else {
          screen = 'detail'
        }
        return {
          name: 'ProximityList',
          query: {
            screen: screen,
          },
        }
      } else {
        return {
          name: 'ProximityDetail',
          params: {
            request_header_id: props.requestHeaderId,
          },
        }
      }
    })
    // バリデーション
    const validate = () => {
      validationError.value = { ...initializedBackValid } // バックバリデーションも初期化
      let result = true
      if (formRef.value) {
        formRef.value.validate((valid) => {
          if (!valid) {
            // チェックNG
            result = false
          }
        })
      }
      return result
    }
    const returnRoute = () => {
      router.push(returnTo.value)
    }

    // 保存処理がキャンセルされた場合の処理
    const notSaved = (backValidResponse?: AxiosResponse | null) => {
      isSave.value = false
      isValid.value = false
      if (!backValidResponse) {
        return
      }
      displayBackValid(backValidResponse)
    }

    // バックバリデーションを表示
    const displayBackValid = (backValidResponse: AxiosResponse) => {
      // カスタムバックバリデーションのレスポンスタイプ変換
      type customBackValidResponseType = {
        [key: string]: string[]
      }
      // 型変換(そのままではブラケット記法を利用できないため)
      const changedResponseType = backValidResponse as unknown
      const customBackValidResponse = changedResponseType as customBackValidResponseType

      // テンプレートに配置済のバックバリデーションのキーをループ
      Object.keys(initializedBackValid).forEach((fixedKey) => {
        // カスタムバリデーションを取得
        if (fixedKey in customBackValidResponse) {
          // エラーレスポンス中にキーが存在する場合
          validationError.value[fixedKey] = customBackValidResponse[fixedKey][0]
        }
      })
    }

    return {
      isSave,
      isValid,
      validate,
      isShowDialog,
      isAfterToday,
      proximityId,
      availableLabels,
      constructionTypeOther: ConstructionTypes.OTHER,
      FileTypes,
      formRef,
      form,
      mapLoad,
      rules: proximityEditRule,
      mapCollapseChange: () => {
        // MapのCollapseが変化するとmap読み込みを有効化する
        mapLoad.value = true
      },
      returnTo,
      returnRoute,
      MessageTypes,
      targetMessageTypes,
      location_map_file_ref,
      ground_plan_file_ref,
      cross_section_file_ref,
      actual_place_photo_file_ref,
      foundation_drawing_file_ref,
      plan_temporary_work_file_ref,
      working_range_file_ref,
      cadastral_map_file_ref,
      survey_drawing_of_acreage_file_ref,
      fileRefs,
      errorFoundationDrawingFile,
      errorWorkingRangeFile,
      mapCenterLat,
      mapCenterLng,
      mapZoom,
      changeMapCenter,
      sumpleSubmissionsUrl: process.env.VUE_APP_PLOXIMITY_SUMPLE_SUBMISSIONS_URL,
      allowExts: APPLICANT_UPLOAD_FILE_EXTS,
      notSaved,
      validationError,
    }
  },
})
